// Frontend console logging for patient-signup / verification failures.
//
// When a verification code fails to send, the toast only says "Could not send
// verification code". This prints the backend's plain-English health report to
// the browser console instead — naming the exact missing env var, unapplied SQL
// migration (missing table/column), stale schema cache, or dead mail/SMS token,
// each with a "FIX" line. So the reason is visible in devtools, not opaque.
//
// The report reflects PRESENCE of secrets only (never their values); it comes
// from GET /api/admin/patient-signup-diagnostics/.

import { getBackendURL } from '../components/env';

// Print an already-fetched diagnostics report to the console.
export function logDiagnosticsReport(report, context = '') {
  const tag = `[patient-signup-debug]${context ? ' ' + context : ''}`;
  if (!report || typeof report !== 'object') {
    console.warn(`${tag} no diagnostics report available`);
    return;
  }
  const problems = (report.checks || []).filter((c) => c.status !== 'ok');
  if (!problems.length) {
    console.log(`${tag} ✅ ${report.summary || 'All prerequisites look OK.'}`);
    return;
  }
  // Grouped so it's one collapsible block in the console, not a wall of lines.
  const groupLabel = `${tag} ${report.summary || 'signup configuration problems found'}`;
  (console.groupCollapsed || console.log).call(console, groupLabel);
  problems.forEach((c) => {
    const isError = c.status === 'error';
    const label = isError ? '❌ FAIL' : '⚠️ WARN';
    const style = isError ? 'color:#c0392b;font-weight:bold' : 'color:#c67600;font-weight:bold';
    console.log(`%c${label}%c ${c.name}: ${c.message}`, style, 'color:inherit');
    if (c.fix) console.log(`        → FIX: ${c.fix}`);
  });
  if (console.groupEnd) console.groupEnd();
}

// Fetch the health report and print it to the console. `live` also authenticates
// against the email/SMS providers (no message is sent) to catch dead tokens.
export async function fetchAndLogPatientSignupDiagnostics(context = '', { live = true } = {}) {
  const tag = `[patient-signup-debug]${context ? ' ' + context : ''}`;
  try {
    const res = await fetch(
      `${getBackendURL()}/api/admin/patient-signup-diagnostics/${live ? '?live=1' : ''}`,
    );
    const report = await res.json();
    logDiagnosticsReport(report, context);
    return report;
  } catch (e) {
    console.warn(`${tag} could not fetch diagnostics: ${e && e.message ? e.message : e}`);
    return null;
  }
}
